// Mobile touch controls: a floating left joystick (movement), Enter + PSI buttons
// (PSI hold maps to K_SP), and contextual buttons (Map/Esc/Help/Settings).
// Feeds the same input layer the keyboard uses.

import { keyHeld, pushEvent, type Sym } from '../core/input';
import { K_UP, K_DN, K_LT, K_RT, K_SP, RES_SCALE } from '../core/constants';
import { enableAudio, ensureMusicPlaying } from '../audio/index';
import { onViewLayout } from '../render/display';
import { isCoarsePointer } from './device';

const KNOB_RATIO = 0.5;
const JOY_RING_RATIO = 0.72;

let joyId = -1;
let joyCx = 0;
let joyCy = 0;
let joyRadius = 60;
let joyDead = 18;
let knobSize = 30;
let knobRaf = 0;
let pendingKnobDx = 0;
let pendingKnobDy = 0;

let joyBase!: HTMLDivElement;
let joyRing!: HTMLDivElement;
let joyKnob!: HTMLDivElement;
let psiBtn!: HTMLDivElement;
let enterBtn!: HTMLDivElement;
let mapBtn!: HTMLDivElement;
let escBtn!: HTMLDivElement;
let helpBtn!: HTMLDivElement;
let settingsBtn!: HTMLDivElement;
let touchLayer!: HTMLDivElement;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function el(tag: string, css: Partial<CSSStyleDeclaration>, parent: HTMLElement): HTMLDivElement {
  const d = document.createElement(tag) as HTMLDivElement;
  Object.assign(d.style, css);
  parent.appendChild(d);
  return d;
}

function setDir(dx: number, dy: number): void {
  const mag = Math.sqrt(dx * dx + dy * dy);
  keyHeld[K_UP] = 0;
  keyHeld[K_DN] = 0;
  keyHeld[K_LT] = 0;
  keyHeld[K_RT] = 0;
  if (mag < joyDead) return;
  const ax = joyRadius * 0.34;
  if (dy < -ax) keyHeld[K_UP] = 1;
  if (dy > ax) keyHeld[K_DN] = 1;
  if (dx < -ax) keyHeld[K_LT] = 1;
  if (dx > ax) keyHeld[K_RT] = 1;
}

function scheduleKnobMove(dx: number, dy: number): void {
  pendingKnobDx = dx;
  pendingKnobDy = dy;
  if (knobRaf) return;
  knobRaf = requestAnimationFrame(() => {
    knobRaf = 0;
    const m = Math.min(Math.sqrt(pendingKnobDx ** 2 + pendingKnobDy ** 2), joyRadius);
    const a = Math.atan2(pendingKnobDy, pendingKnobDx);
    const center = joyRadius - knobSize / 2;
    joyKnob.style.left = `${center + Math.cos(a) * m}px`;
    joyKnob.style.top = `${center + Math.sin(a) * m}px`;
  });
}

function pulse(sym: Sym): void {
  pushEvent({ down: true, sym });
  pushEvent({ down: false, sym });
}

function applyTouchLayout(vw: number, vh: number): void {
  const vmin = Math.min(vw, vh);
  const margin = clamp(vmin * 0.04, 12, 24);
  const safeTop = `max(${margin}px, env(safe-area-inset-top, 0px))`;
  const safeBottom = `max(${margin}px, env(safe-area-inset-bottom, 0px))`;
  const safeLeft = `max(${margin}px, env(safe-area-inset-left, 0px))`;
  const safeRight = `max(${margin}px, env(safe-area-inset-right, 0px))`;

  joyRadius = clamp(vmin * 0.07 * RES_SCALE, 48 * RES_SCALE, 72 * RES_SCALE);
  joyDead = joyRadius * 0.3;
  const joyD = joyRadius * 2;
  const ringD = joyRadius * JOY_RING_RATIO * 2;
  const ringOffset = (joyD - ringD) / 2;
  knobSize = joyRadius * KNOB_RATIO;
  const btnH = clamp(vmin * 0.06, 40, 52);
  const btnW = clamp(vmin * 0.08, 48, 64);
  const psiSize = clamp(vmin * 0.14, 88, 120);
  const fontSize = clamp(vmin * 0.028, 14, 18);
  const hudClearance = btnH + margin;
  const btnTop = `calc(${safeTop} + ${hudClearance}px)`;

  joyBase.style.left = safeLeft;
  joyBase.style.bottom = safeBottom;
  joyBase.style.width = `${joyD}px`;
  joyBase.style.height = `${joyD}px`;

  joyRing.style.width = `${ringD}px`;
  joyRing.style.height = `${ringD}px`;
  joyRing.style.left = `${ringOffset}px`;
  joyRing.style.top = `${ringOffset}px`;

  const center = joyRadius - knobSize / 2;
  joyKnob.style.width = `${knobSize}px`;
  joyKnob.style.height = `${knobSize}px`;
  joyKnob.style.left = `${center}px`;
  joyKnob.style.top = `${center}px`;

  psiBtn.style.right = safeRight;
  psiBtn.style.bottom = safeBottom;
  psiBtn.style.width = `${psiSize}px`;
  psiBtn.style.height = `${psiSize}px`;

  const sideBtn = { width: `${btnW}px`, height: `${btnH}px`, fontSize: `${fontSize}px` };
  Object.assign(enterBtn.style, sideBtn);
  enterBtn.style.right = `calc(${safeRight} + ${psiSize + margin}px)`;
  enterBtn.style.bottom = `calc(${safeBottom} + ${margin}px)`;

  Object.assign(mapBtn.style, sideBtn);
  Object.assign(escBtn.style, sideBtn);
  Object.assign(helpBtn.style, { width: `${btnH}px`, height: `${btnH}px`, fontSize: `${fontSize}px` });
  Object.assign(settingsBtn.style, { width: `${btnH}px`, height: `${btnH}px`, fontSize: `${fontSize}px` });

  mapBtn.style.right = safeRight;
  mapBtn.style.top = btnTop;

  escBtn.style.left = safeLeft;
  escBtn.style.top = btnTop;

  helpBtn.style.left = `calc(${safeLeft} + ${btnW + margin * 0.5}px)`;
  helpBtn.style.top = btnTop;

  settingsBtn.style.left = `calc(${safeLeft} + ${(btnW + margin * 0.5) * 2}px)`;
  settingsBtn.style.top = btnTop;
}

function initRotateHint(): void {
  const hint = document.getElementById('rotate-hint');
  const root = document.getElementById('game-root');
  if (!hint || !root) return;

  const update = () => {
    const portrait = window.matchMedia('(orientation: portrait)').matches;
    hint.style.display = portrait ? 'flex' : 'none';
    root.style.visibility = portrait ? 'hidden' : 'visible';
    touchLayer.style.display = portrait ? 'none' : 'block';
  };

  window.matchMedia('(orientation: portrait)').addEventListener('change', update);
  window.addEventListener('resize', update);
  update();
}

export function initTouch(): void {
  if (!isCoarsePointer()) return;

  touchLayer = el(
    'div',
    {
      position: 'fixed',
      inset: '0',
      zIndex: '20',
      touchAction: 'none',
    },
    document.body
  );
  touchLayer.style.pointerEvents = 'none';

  const btnBase: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#bfe6ff',
    background: 'rgba(30,60,90,0.35)',
    border: '2px solid rgba(140,200,255,0.5)',
    borderRadius: '12px',
    fontFamily: 'monospace',
    fontSize: '16px',
    userSelect: 'none',
    pointerEvents: 'auto',
    touchAction: 'none',
  };

  joyBase = el(
    'div',
    {
      position: 'fixed',
      pointerEvents: 'auto',
      touchAction: 'none',
    },
    touchLayer
  );
  joyRing = el(
    'div',
    {
      position: 'absolute',
      borderRadius: '50%',
      background: 'rgba(30,60,90,0.3)',
      border: '2px solid rgba(140,200,255,0.4)',
      pointerEvents: 'none',
    },
    joyBase
  );
  joyKnob = el(
    'div',
    {
      position: 'absolute',
      borderRadius: '50%',
      background: 'rgba(140,200,255,0.5)',
    },
    joyBase
  );

  joyBase.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    enableAudio();
    ensureMusicPlaying();
    const r = joyBase.getBoundingClientRect();
    joyCx = r.left + r.width / 2;
    joyCy = r.top + r.height / 2;
    joyId = e.pointerId;
    joyBase.setPointerCapture(e.pointerId);
    setDir(e.clientX - joyCx, e.clientY - joyCy);
    scheduleKnobMove(e.clientX - joyCx, e.clientY - joyCy);
  });
  joyBase.addEventListener('pointermove', (e) => {
    if (e.pointerId !== joyId) return;
    e.preventDefault();
    setDir(e.clientX - joyCx, e.clientY - joyCy);
    scheduleKnobMove(e.clientX - joyCx, e.clientY - joyCy);
  });
  const joyEnd = (e: PointerEvent) => {
    if (e.pointerId !== joyId) return;
    joyId = -1;
    setDir(0, 0);
    scheduleKnobMove(0, 0);
  };
  joyBase.addEventListener('pointerup', joyEnd);
  joyBase.addEventListener('pointercancel', joyEnd);

  psiBtn = el('div', { ...btnBase, borderRadius: '50%' }, touchLayer);
  psiBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    enableAudio();
    ensureMusicPlaying();
    psiBtn.setPointerCapture(e.pointerId);
    keyHeld[K_SP] = 1;
  });
  const psiEnd = (e: PointerEvent) => {
    e.preventDefault();
    keyHeld[K_SP] = 0;
  };
  psiBtn.addEventListener('pointerup', psiEnd);
  psiBtn.addEventListener('pointercancel', psiEnd);

  enterBtn = el('div', { ...btnBase }, touchLayer);
  enterBtn.textContent = '↵';
  enterBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    pulse('RETURN');
  });

  mapBtn = el('div', { ...btnBase }, touchLayer);
  mapBtn.textContent = 'MAP';
  mapBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    pulse('TAB');
  });

  escBtn = el('div', { ...btnBase }, touchLayer);
  escBtn.textContent = 'ESC';
  escBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    pulse('ESCAPE');
  });

  helpBtn = el('div', { ...btnBase }, touchLayer);
  helpBtn.textContent = '?';
  helpBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    pulse('H');
  });

  settingsBtn = el('div', { ...btnBase }, touchLayer);
  settingsBtn.textContent = 'I';
  settingsBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    pulse('I');
  });

  onViewLayout((v) => applyTouchLayout(v.vw, v.vh));
  initRotateHint();
}
